import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, jobs, type Job } from '@/lib/mocks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PageHeader } from '@/lib/atsUi'
import { DataList } from '@/components/ui/data-list'
import { Sparkles, MapPin, Briefcase, Users, IndianRupee, Calendar, Plus, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/jobs/')({
  component: JobsPage,
})

function statusVariant(status: Job['status']): 'success' | 'warning' | 'info' | 'error' {
  switch (status) {
    case 'open': return 'success'
    case 'on_hold': return 'warning'
    case 'filled': return 'info'
    case 'draft': return 'info'
  }
}

function statusLabel(status: Job['status']): string {
  switch (status) {
    case 'open': return 'Open'
    case 'on_hold': return 'On hold'
    case 'filled': return 'Filled'
    case 'draft': return 'Draft'
  }
}

interface JdSection {
  about: string[]
  responsibilities: string[]
  requirements: string[]
}

function generateJd(job: Job): JdSection {
  const title = job.title.toLowerCase()

  if (title.includes('product manager') || title.includes('product')) {
    return {
      about: [
        `${job.client} is looking for an experienced Product Manager to own the roadmap and drive product strategy across key business verticals.`,
        `You will work closely with engineering, design, and business stakeholders to define, prioritise, and ship features that create measurable customer impact.`,
      ],
      responsibilities: [
        'Define and communicate a clear product vision and roadmap aligned with company OKRs',
        'Conduct user research, analyse data, and synthesise insights to inform prioritisation decisions',
        'Write crisp PRDs and user stories; drive sprint planning alongside engineering leads',
        'Own end-to-end feature delivery from discovery through launch and iteration',
        'Partner with sales and customer success to capture market signals and close feedback loops',
      ],
      requirements: [
        '6+ years of product management experience in a B2B SaaS or fintech environment',
        'Demonstrated track record of shipping 0-to-1 products and iterating post-launch',
        'Strong analytical skills with hands-on experience in SQL, Mixpanel, or equivalent tools',
        'Excellent written and verbal communication; able to influence without authority',
        'MBA or engineering background preferred; experience with agile frameworks is a must',
      ],
    }
  }

  if (title.includes('data') && title.includes('engineer')) {
    return {
      about: [
        `${job.client} is hiring a Senior Data Engineer to build and maintain scalable data pipelines that power analytics and ML initiatives across the organisation.`,
        `You will be the cornerstone of the data platform team, ensuring data reliability, quality, and accessibility for both technical and business consumers.`,
      ],
      responsibilities: [
        'Design and implement robust ELT/ETL pipelines using Spark, dbt, and Airflow',
        'Architect and maintain a modern data lakehouse on cloud infrastructure (AWS/GCP)',
        'Collaborate with data scientists to productionise ML feature pipelines',
        'Define data contracts, schema governance, and observability standards',
        'Mentor junior engineers and conduct thorough code and design reviews',
      ],
      requirements: [
        '5+ years of data engineering experience with strong Python and SQL skills',
        'Hands-on expertise with Apache Spark, dbt, and workflow orchestration tools like Airflow or Prefect',
        'Experience with cloud data warehouses such as BigQuery, Snowflake, or Redshift',
        'Solid understanding of data modelling, Kimball methodology, and streaming architectures',
        'Experience in a fast-paced startup or high-growth environment is strongly preferred',
      ],
    }
  }

  if (title.includes('frontend') || title.includes('react') || title.includes('ui')) {
    return {
      about: [
        `${job.client} is looking for a Senior Frontend Engineer to lead the development of high-performance, accessible user interfaces used by thousands of customers daily.`,
        `You will set the bar for frontend quality, define component architecture, and mentor a growing team of engineers building world-class product experiences.`,
      ],
      responsibilities: [
        'Architect and build scalable React component libraries and design system integrations',
        'Drive performance optimisations — Core Web Vitals, bundle size, rendering efficiency',
        'Collaborate with product designers to translate complex UX requirements into clean implementations',
        'Establish testing standards using Vitest and Playwright across the frontend stack',
        'Lead technical interviews and actively contribute to hiring bar decisions',
      ],
      requirements: [
        '6+ years of frontend engineering with deep React and TypeScript expertise',
        'Strong command of web performance, accessibility (WCAG 2.1), and browser internals',
        'Experience building and maintaining design systems at scale',
        'Familiarity with state management patterns (Zustand, Redux, TanStack Query)',
        'Prior experience in a product-led growth company is a significant plus',
      ],
    }
  }

  if (title.includes('backend') || title.includes('node') || title.includes('java') || title.includes('python')) {
    return {
      about: [
        `${job.client} is seeking a Senior Backend Engineer to design and deliver reliable, high-throughput services that form the backbone of its core platform.`,
        `You will work in a cross-functional squad, owning entire service domains from architecture through to production operations.`,
      ],
      responsibilities: [
        'Design and implement microservices with a focus on reliability, latency, and scalability',
        'Define API contracts and integrate with third-party systems and payment gateways',
        'Participate in on-call rotations; own incident response and post-mortems for your domain',
        'Improve observability through structured logging, distributed tracing, and alerting',
        'Champion engineering best practices — code reviews, documentation, and test coverage',
      ],
      requirements: [
        '5+ years of backend engineering; proficient in Node.js, Java, or Python',
        'Experience building and operating distributed systems in production at scale',
        'Strong understanding of relational databases, query optimisation, and indexing strategies',
        'Familiarity with message queues (Kafka, RabbitMQ) and event-driven architecture',
        'Track record of delivering high-quality software in agile, collaborative teams',
      ],
    }
  }

  return {
    about: [
      `${job.client} is hiring a ${job.title} to drive meaningful outcomes across a fast-growing business operating in a competitive market.`,
      `This is a high-impact role where you will work alongside talented peers and senior leadership to shape strategy and execution at scale.`,
    ],
    responsibilities: [
      `Lead end-to-end ownership of key ${job.title} deliverables with measurable KPIs`,
      'Collaborate cross-functionally with product, engineering, and business stakeholders',
      'Identify process gaps and implement structured improvements for team efficiency',
      'Mentor junior team members and contribute to a high-performance team culture',
      'Report progress to leadership and proactively surface risks and mitigation plans',
    ],
    requirements: [
      `6+ years of progressive experience in a ${job.title} or closely related function`,
      'Proven ability to operate autonomously and deliver under ambiguity',
      'Strong communication skills with the ability to influence senior decision-makers',
      'Data-driven mindset with experience using analytics to guide decisions',
      'Prior experience at a growth-stage startup or mid-market company is preferred',
    ],
  }
}

function FactRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  )
}

function JobsPage() {
  const [selectedId, setSelectedId] = useState<string>(jobs[0].id)
  const selected = jobs.find((j) => j.id === selectedId) ?? jobs[0]
  const jd = generateJd(selected)

  return (
    <DashboardLayout
      title="Jobs"
      navSections={atsNav}
      tenant={currentTenant}
      user={currentUser}
    >
      <PageHeader
        title="Jobs"
        subtitle="Open roles and the descriptions the AI drafted."
        action={
          <Button size="sm" asChild>
            <Link to="/jobs/new">
              <Plus className="h-4 w-4 mr-1" />
              New job
            </Link>
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
        {/* Left pane — job list */}
        <DataList
          items={jobs}
          getKey={(j) => j.id}
          pageSize={5}
          empty="No jobs."
          renderRow={(job) => (
            <div className="relative flex items-stretch">
              <button
                onClick={() => setSelectedId(job.id)}
                className={cn(
                  'flex-1 min-w-0 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                  selectedId === job.id
                    ? 'bg-muted/50 border-l-2 border-l-primary'
                    : 'border-l-2 border-l-transparent',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-foreground leading-snug">
                    {job.title}
                  </span>
                  <span
                    className="shrink-0"
                    title={job.status === 'on_hold' ? 'Hiring paused — open the role to change status' : undefined}
                  >
                    <Badge variant={statusVariant(job.status)}>
                      {statusLabel(job.status)}
                    </Badge>
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {job.client} · {job.location}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {job.candidates} candidates · {job.ctcRange} · {job.postedAt}
                </p>
              </button>
              <Link
                to="/jobs/$jobId"
                params={{ jobId: job.id }}
                className="flex items-center px-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={`Open ${job.title}`}
              >
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        />

        {/* Right pane — job detail */}
        <Card className="shadow-sm">
          <CardContent className="pt-5 space-y-5">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1">
                <h2 className="text-base font-semibold text-foreground leading-snug">{selected.title}</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">{selected.client}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span title={selected.status === 'on_hold' ? 'Hiring paused — open the role to change status' : undefined}>
                  <Badge variant={statusVariant(selected.status)}>
                    {statusLabel(selected.status)}
                  </Badge>
                </span>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/jobs/$jobId" params={{ jobId: selected.id }}>View candidates</Link>
                </Button>
              </div>
            </div>

            {/* Fact grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
              <FactRow icon={<MapPin className="h-4 w-4" />} label="Location" value={selected.location} />
              <FactRow icon={<IndianRupee className="h-4 w-4" />} label="CTC" value={selected.ctcRange} />
              <FactRow icon={<Briefcase className="h-4 w-4" />} label="Fee" value={`${selected.feePct}%`} />
              <FactRow icon={<Users className="h-4 w-4" />} label="Openings" value={String(selected.openings)} />
              <FactRow icon={<Users className="h-4 w-4" />} label="Candidates" value={String(selected.candidates)} />
              <FactRow icon={<Calendar className="h-4 w-4" />} label="Posted" value={selected.postedAt} />
            </div>

            {/* AI-generated JD block */}
            <div className="rounded-lg border border-border bg-muted/20 p-4">
              <div className="mb-4 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Job description</span>
                <Badge variant="info">AI-generated</Badge>
              </div>

              <div className="space-y-4 text-sm">
                <section>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">About the role</h3>
                  {jd.about.map((line, i) => (
                    <p key={i} className={cn('text-foreground', i > 0 && 'mt-1.5')}>
                      {line}
                    </p>
                  ))}
                </section>

                <section>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">What you'll do</h3>
                  <ul className="list-disc space-y-1 pl-4 text-foreground">
                    {jd.responsibilities.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">What we're looking for</h3>
                  <ul className="list-disc space-y-1 pl-4 text-foreground">
                    {jd.requirements.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </section>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
